import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  TestTube2,
  Copy,
  Trash2,
  CheckCircle,
  FileText,
  ChevronDown,
  ChevronUp,
  Eye,
  Edit,
  X,
  Play,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { ConfirmModal } from '../../components/ConfirmModal';
import { TestCaseListSkeleton, ErrorState, EmptyState } from '../../components/ui';

interface TestCase {
  id: string;
  name: string;
  code: string;
  category: string;
  complexity: string;
  condition: string;
  description?: string;
  tags: string[];
  isDraft: boolean;
  isActive: boolean;
  validatedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; name: string; email: string };
  _count: { testRuns: number; documents: number };
}

type SortField = 'name' | 'category' | 'complexity' | 'updatedAt' | 'testRuns';
type SortDirection = 'asc' | 'desc';

const CATEGORIES = [
  'ORTHOPEDIC',
  'CARDIAC',
  'NEUROLOGICAL',
  'ONCOLOGY',
  'GASTROENTEROLOGY',
  'PULMONOLOGY',
  'ENDOCRINE',
  'RHEUMATOLOGY',
  'DERMATOLOGY',
  'PSYCHIATRIC',
  'PEDIATRIC',
  'GERIATRIC',
  'OBSTETRIC',
  'UROLOGICAL',
  'OTHER',
];

const COMPLEXITIES = ['SIMPLE', 'MODERATE', 'COMPLEX', 'EDGE_CASE'];

function ComplexityBadge({ complexity }: { complexity: string }) {
  const config: Record<string, { color: string; label: string }> = {
    SIMPLE: { color: 'bg-green-100 text-green-700', label: 'Simple' },
    MODERATE: { color: 'bg-blue-100 text-blue-700', label: 'Moderate' },
    COMPLEX: { color: 'bg-amber-100 text-amber-700', label: 'Complex' },
    EDGE_CASE: { color: 'bg-red-100 text-red-700', label: 'Edge Case' },
  };
  const cfg = config[complexity] || { color: 'bg-gray-100 text-gray-700', label: complexity };
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium', cfg.color)}>
      {cfg.label}
    </span>
  );
}

function StatusIndicator({ isDraft, isActive, validatedAt }: { isDraft: boolean; isActive: boolean; validatedAt?: string }) {
  if (!isActive) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
        Archived
      </span>
    );
  }
  if (isDraft) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <FileText className="h-3 w-3" />
        Draft
      </span>
    );
  }
  if (validatedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="h-3 w-3" />
        Validated
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <TestTube2 className="h-3 w-3" />
      Ready
    </span>
  );
}

interface SortableHeaderProps {
  label: string;
  sortKey: SortField;
  currentSort: { field: SortField; direction: SortDirection } | null;
  onSort: (field: SortField) => void;
  className?: string;
}

function SortableHeader({ label, sortKey, currentSort, onSort, className }: SortableHeaderProps) {
  const isActive = currentSort?.field === sortKey;
  const direction = isActive ? currentSort.direction : null;

  return (
    <th
      className={clsx(
        'px-4 py-3 text-left cursor-pointer select-none hover:bg-gray-100 transition-colors',
        className
      )}
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
        <div className="flex flex-col -space-y-1">
          <ChevronUp
            className={clsx(
              'h-3 w-3',
              direction === 'asc' ? 'text-amber-600' : 'text-gray-300'
            )}
          />
          <ChevronDown
            className={clsx(
              'h-3 w-3',
              direction === 'desc' ? 'text-amber-600' : 'text-gray-300'
            )}
          />
        </div>
      </div>
    </th>
  );
}

interface FilterPillProps {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}

function FilterPill({ label, value, options, onChange }: FilterPillProps) {
  const isActive = !!value;

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={clsx(
          'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
          isActive
            ? 'border-amber-300 bg-amber-50 text-amber-700'
            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
        )}
        style={{ backgroundImage: 'none' }}
      >
        <option value="">{label}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className={clsx(
          'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
          isActive ? 'text-amber-500' : 'text-gray-400'
        )}
      />
    </div>
  );
}

type ViewFilter = 'active' | 'archived' | 'all';

export function TestCaseList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [sort, setSort] = useState<{ field: SortField; direction: SortDirection } | null>(null);

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const complexity = searchParams.get('complexity') || '';
  const status = searchParams.get('status') || '';
  // Default view is 'active' (hide archived), can be 'archived' or 'all'
  const view = (searchParams.get('view') as ViewFilter) || 'active';

  const activeFilterCount = [category, complexity, status].filter(Boolean).length;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['validation', 'test-cases', { search, category, complexity, status, view }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      if (complexity) params.set('complexity', complexity);
      if (status === 'draft') params.set('isDraft', 'true');
      if (status === 'validated') params.set('isDraft', 'false');
      // Handle view filter (default: active only)
      if (view === 'active') {
        params.set('isActive', 'true');
      } else if (view === 'archived') {
        params.set('isActive', 'false');
      }
      // 'all' view: don't set isActive filter
      params.set('limit', '1000');
      return api.get<{ data: TestCase[]; pagination: { total: number; limit: number; offset: number } }>(
        `/api/validation/test-cases?${params.toString()}`
      );
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/validation/test-cases/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-cases'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/validation/test-cases/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-cases'] });
    },
  });

  const testCases: TestCase[] = data?.data || [];
  const pagination = data?.pagination || { total: 0 };

  // Client-side sorting
  const sortedTestCases = useMemo(() => {
    if (!sort) return testCases;

    return [...testCases].sort((a, b) => {
      let comparison = 0;
      switch (sort.field) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        case 'complexity': {
          const complexityOrder = { SIMPLE: 1, MODERATE: 2, COMPLEX: 3, EDGE_CASE: 4 };
          comparison = (complexityOrder[a.complexity as keyof typeof complexityOrder] || 0) -
                       (complexityOrder[b.complexity as keyof typeof complexityOrder] || 0);
          break;
        }
        case 'updatedAt':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        case 'testRuns':
          comparison = a._count.testRuns - b._count.testRuns;
          break;
      }
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [testCases, sort]);

  const handleSort = (field: SortField) => {
    setSort((current) => {
      if (current?.field === field) {
        if (current.direction === 'asc') {
          return { field, direction: 'desc' };
        }
        return null; // Reset sorting
      }
      return { field, direction: 'asc' };
    });
  };

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const clearAllFilters = () => {
    // Keep the view filter when clearing, default to 'active'
    const params = new URLSearchParams();
    if (view !== 'active') {
      params.set('view', view);
    }
    setSearchParams(params);
    setSort(null);
  };

  const setView = (newView: ViewFilter) => {
    const params = new URLSearchParams(searchParams);
    if (newView === 'active') {
      params.delete('view'); // 'active' is default, don't need in URL
    } else {
      params.set('view', newView);
    }
    // Clear status filter when changing view to avoid conflicts
    params.delete('status');
    setSearchParams(params);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Test Cases</h1>
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
        <TestCaseListSkeleton rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Test Cases</h1>
          </div>
        </div>
        <ErrorState
          title="Failed to load test cases"
          message="Unable to load test cases. Please check your connection and try again."
          onRetry={() => refetch()}
          variant="card"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Test Cases</h1>
          <p className="text-sm text-gray-500">
            {pagination.total} test case{pagination.total !== 1 ? 's' : ''}
            {view === 'archived' && ' (archived)'}
            {view === 'all' && ' (all)'}
            {activeFilterCount > 0 && ` · filtered`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              onClick={() => setView('active')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                view === 'active'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Active
            </button>
            <button
              onClick={() => setView('archived')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                view === 'archived'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Archived
            </button>
            <button
              onClick={() => setView('all')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                view === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              All
            </button>
          </div>
          <Link
            to="/validation/test-cases/new"
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Case
          </Link>
        </div>
      </div>

      {/* Enhanced Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {/* Search with clear button */}
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search test cases by name, code, or condition..."
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
          />
          {search && (
            <button
              onClick={() => updateFilter('search', '')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap gap-2">
          <FilterPill
            label="Category"
            value={category}
            options={CATEGORIES.map((cat) => ({
              value: cat,
              label: cat.replace(/_/g, ' '),
            }))}
            onChange={(v) => updateFilter('category', v)}
          />
          <FilterPill
            label="Complexity"
            value={complexity}
            options={COMPLEXITIES.map((comp) => ({
              value: comp,
              label: comp.replace(/_/g, ' '),
            }))}
            onChange={(v) => updateFilter('complexity', v)}
          />
          <FilterPill
            label="Status"
            value={status}
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'validated', label: 'Validated' },
            ]}
            onChange={(v) => updateFilter('status', v)}
          />
        </div>

        {/* Active filters summary */}
        {(activeFilterCount > 0 || search || sort) && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors"
          >
            <X className="h-4 w-4" />
            Clear all
          </button>
        )}
      </div>

      {/* List */}
      {sortedTestCases.length === 0 ? (
        <EmptyState
          icon={TestTube2}
          title="No test cases found"
          description={
            search || category || complexity || status
              ? 'Try adjusting your filters to find what you\'re looking for'
              : 'Create your first test case to get started with model validation'
          }
          action={
            !search && !category && !complexity && !status
              ? {
                  label: 'Create Test Case',
                  href: '/validation/test-cases/new',
                  icon: Plus,
                }
              : undefined
          }
          secondaryAction={
            (search || category || complexity || status)
              ? {
                  label: 'Clear Filters',
                  onClick: clearAllFilters,
                  variant: 'secondary',
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader
                    label="Test Case"
                    sortKey="name"
                    currentSort={sort}
                    onSort={handleSort}
                    className="min-w-[200px]"
                  />
                  <SortableHeader
                    label="Category"
                    sortKey="category"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Complexity"
                    sortKey="complexity"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <SortableHeader
                    label="Runs"
                    sortKey="testRuns"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Updated"
                    sortKey="updatedAt"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedTestCases.map((tc) => (
                  <tr key={tc.id} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-4 py-3">
                      <Link to={`/validation/test-cases/${tc.id}`} className="block">
                        <div className="font-medium text-gray-900 group-hover:text-amber-600 transition-colors">
                          {tc.name}
                        </div>
                        <div className="text-sm text-gray-500 font-mono">{tc.code}</div>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700 capitalize">
                        {tc.category.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ComplexityBadge complexity={tc.complexity} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusIndicator
                        isDraft={tc.isDraft}
                        isActive={tc.isActive}
                        validatedAt={tc.validatedAt}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        <Play className="h-3 w-3" />
                        {tc._count.testRuns}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">
                        {formatDistanceToNow(new Date(tc.updatedAt), { addSuffix: true })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        <Link
                          to={`/validation/test-cases/${tc.id}`}
                          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/validation/test-cases/${tc.id}/edit`}
                          className="rounded p-1.5 text-gray-400 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        {!tc.isDraft && tc.isActive ? (
                          <Link
                            to={`/validation/test-runs/new?testCaseId=${tc.id}`}
                            className="rounded p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-600 transition-colors"
                            title="Start Test Run"
                          >
                            <Play className="h-4 w-4" />
                          </Link>
                        ) : (
                          <span
                            className="rounded p-1.5 text-gray-300 cursor-not-allowed"
                            title={tc.isDraft ? "Complete and activate this test case first" : "Activate this test case first"}
                          >
                            <Play className="h-4 w-4" />
                          </span>
                        )}
                        <button
                          onClick={() => duplicateMutation.mutate(tc.id)}
                          className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                          title="Duplicate"
                          disabled={duplicateMutation.isPending}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(tc.id)}
                          className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Archive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => {
          if (deleteConfirmId) {
            deleteMutation.mutate(deleteConfirmId);
            setDeleteConfirmId(null);
          }
        }}
        title="Archive Test Case"
        message="Are you sure you want to archive this test case? This action can be undone later."
        confirmText="Archive"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
